import { Injectable } from '@angular/core';

import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPlaceBetActions } from '../../betplacement/actions';
import BetslipPickFactory from '../../picks/factory/betslip-pick.factory';
import SettingsActions from '../../settings/actions';
import DigitalSettingsActions from '../../settings/digital-actions';
import { BetslipTypesActions } from '../../types/actions';
import { BetslipDigitalTrackingService } from '../services/betslip-digital-tracking.service';
import { BetslipLaunchDarklyTrackingService } from '../services/betslip-launch-darkly-tracking.service';
import BetslipBaseTrackingEffects from './betslip-base-tracking.effects';
import BetslipTrackingActions from './tracking.actions';

@Injectable()
export default class BetslipDigitalTrackingEffects extends BetslipBaseTrackingEffects {
    constructor(
        actions$: Actions,
        store: Store<IBetslipRootState>,
        pickFactory: BetslipPickFactory,
        protected override betslipTrackingService: BetslipDigitalTrackingService,
        private betslipLaunchDarklyTrackingService: BetslipLaunchDarklyTrackingService,
    ) {
        super(actions$, store, betslipTrackingService, pickFactory);
    }

    handlePlaceBetTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipTrackingActions.checkoutSlip),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([action, state]) => {
                    this.betslipTrackingService.trackBetPlacementStart(state, action.requestId, action.slips);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleSetSelectedTypeTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipTypesActions.setSelectedType),
                filter((action) => action.selectedByUser),
                tap((action) => this.betslipTrackingService.trackBetTypeChange(action.betslipType)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleOpenSettingTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(DigitalSettingsActions.openSettings),
                tap(() => this.betslipTrackingService.trackOpenSettings()),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleOddAcceptanceSettingTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(SettingsActions.setOddAcceptance),
                tap((action) => this.betslipTrackingService.trackOddAcceptanceChange(action.acceptance)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleEmailNotifySettingTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(SettingsActions.setEmailNotify),
                tap((action) => this.betslipTrackingService.trackEmailNotificationSettingChange(action.value)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleAppNotifySettingTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(SettingsActions.setAppNotify),
                tap((action) => this.betslipTrackingService.trackAppNotificationSettingChange(action.value)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleFinishPlaceBetLaunchDarklyTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPlaceBetActions.trackBetPlacementResult),
                filter((action) => action.state.types.base.currentSelectedType !== BetslipType.EditBet),
                tap((action) => this.betslipLaunchDarklyTrackingService.trackSucceededSlips(action.placeBetResult)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );
}
